/**
 * classifyMutateError — pure error classifier for mutate handlers.
 *
 * Splits any error thrown from a mutate call (oRPC client error, network
 * exception, unknown shape) into one of two kinds:
 *
 *   - `user`   — actionable by the user (rename, change input, sign in again).
 *                Carries mode-specific copy. Caller surfaces inline; NO
 *                observability event is reported.
 *
 *   - `system` — actual server / network / app failure. Carries generic copy.
 *                Caller reports a structured event so the operator can see
 *                edge cases via `wrangler tail` / Workers Logs.
 *
 * This module is pure: no side effects, no DOM access, no fetch. Same input
 * → same output, deterministically. The reporter (`./report.ts`) is the side-
 * effecting half of the pair; the React hook (`apps/web/src/hooks/useErrorReporter`)
 * is the React-aware glue.
 *
 * Voice: every copy string passes branding.md §5 — sentence-case, no
 * exclamations, no banned words. Mode-specific copy uses the user-facing
 * vocabulary ("Recipe", "Variation", "Spin-off") where applicable.
 *
 * Plan ref: .plan/features/2026-05-04-error-handling-and-observability.md §Design.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

export type UserErrorCode =
  | "slug_taken"
  | "invalid_slug"
  | "quota_exceeded"
  | "ai_ops_exceeded"
  | "group_read_only"
  | "unauthorised"
  | "forbidden"
  | "validation";

export type SystemErrorCode =
  | "network" // fetch threw / aborted
  | "timeout" // 408 or sync commit timeout
  | "server" // 5xx
  | "unknown"; // anything else

export type ClassifiedError =
  | { kind: "user"; code: UserErrorCode; copy: string }
  | { kind: "system"; code: SystemErrorCode; copy: string };

/**
 * Entity type drives copy. Today's MVP first consumer is `recipe` only
 * (see plan §Scope); the other entities are listed up front so the wave-2
 * follow-up does not have to widen this enum across multiple call sites.
 */
export type MutateEntity = "recipe" | "variation" | "collection" | "attempt" | "artefact" | "batch";

export interface ClassifyContext {
  entity: MutateEntity;
  /**
   * Optional sub-mode key — surfaced by RecipeCreationModal as "new" /
   * "spinoff" / "variation". Drives `recipe`-entity copy variants. Other
   * entities ignore it today; reserved for the wave-2 wiring.
   */
  mode?: string;
}

// ── Entity-name vocabulary (branding.md §8) ───────────────────────────────────

/**
 * User-facing noun for "the thing being created/changed". Branding-correct
 * (Variation not branch, Spin-off not fork).
 */
function entityNoun(ctx: ClassifyContext): string {
  if (ctx.entity === "recipe") {
    if (ctx.mode === "spinoff") return "spin-off";
    if (ctx.mode === "variation") return "variation";
    return "recipe";
  }
  if (ctx.entity === "variation") return "variation";
  if (ctx.entity === "collection") return "collection";
  if (ctx.entity === "attempt") return "attempt";
  if (ctx.entity === "artefact") return "ingredient";
  if (ctx.entity === "batch") return "batch";
  return "item";
}

// ── Field readers (defensive — error can be anything) ─────────────────────────

function readNumber(obj: unknown, key: string): number | undefined {
  if (typeof obj !== "object" || obj === null) return undefined;
  const v = (obj as Record<string, unknown>)[key];
  return typeof v === "number" ? v : undefined;
}

function readString(obj: unknown, key: string): string | undefined {
  if (typeof obj !== "object" || obj === null) return undefined;
  const v = (obj as Record<string, unknown>)[key];
  return typeof v === "string" ? v : undefined;
}

// ── Code probing ──────────────────────────────────────────────────────────────

/**
 * The DO returns `error: "slug_taken" | "invalid_slug" | ...` and the oRPC
 * handler propagates it as `ORPCError(<code>, { message: "<error_string>" })`.
 * On the client, the thrown ORPCError carries:
 *   - `status`  — HTTP status (409, 400, 500, etc.)
 *   - `code`    — oRPC error code ("CONFLICT", "BAD_REQUEST", "INTERNAL_SERVER_ERROR")
 *   - `message` — the original error string from the DO (e.g. "slug_taken")
 *
 * We probe the message first because it's the most specific. The status acts
 * as a fallback for cases where the message is not one of our known codes.
 *
 * A route that needs a DISTINCT code while keeping a human `message` puts the
 * machine code in `data.code` instead — `ORPCError("FORBIDDEN", { message: "…",
 * data: { code: "group_read_only" } })` (apps/api/src/routes/groups/shared.ts).
 * `@orpc/server` serialises `error.toJSON()`, so the client's ORPCError carries
 * that `data` object verbatim; `data.code` is therefore a first-class probe
 * site alongside `data.error`.
 */
function findUserErrorCode(err: unknown): UserErrorCode | null {
  const message = readString(err, "message")?.toLowerCase() ?? "";
  // The oRPC error code (e.g. "CONFLICT", or a stable UPPER_SNAKE app code like
  // "AI_OPS_EXCEEDED") is the most specific signal when present.
  const code = readString(err, "code")?.toLowerCase() ?? "";
  // Sometimes `message` is the verbatim DO code; sometimes the DO code is
  // in `cause` or in a structured `data` envelope. Probe the obvious places.
  const causeMessage = readString(readObj(err, "cause"), "message")?.toLowerCase() ?? "";
  const dataError = readString(readObj(err, "data"), "error")?.toLowerCase() ?? "";
  const dataCode = readString(readObj(err, "data"), "code")?.toLowerCase() ?? "";
  const haystack = `${code} ${message} ${causeMessage} ${dataError} ${dataCode}`;

  // Downgrade safety (WP-B): an owner over the owned-groups cap has their
  // newest groups frozen, so headroom-consuming writes (invite, approve,
  // share-into, rename, re-describe/publish, assign moderator) refuse with
  // FORBIDDEN + data.code. Checked BEFORE the generic quota probe so it never
  // inherits the "Upgrade or remove items" remedy — the caller is frequently a
  // MEMBER who owns nothing here and can do nothing about the owner's plan.
  if (haystack.includes("group_read_only")) return "group_read_only";
  if (haystack.includes("slug_taken")) return "slug_taken";
  if (haystack.includes("invalid_slug")) return "invalid_slug";
  // Assistant-ops cap (C-WP4). Checked BEFORE the generic quota probe so a
  // metered-op cap maps to its own reset-aware copy, not the storage/groups
  // "Upgrade or remove items" line.
  if (haystack.includes("ai_ops_exceeded")) return "ai_ops_exceeded";
  if (haystack.includes("quota_exceeded") || haystack.includes("quota")) return "quota_exceeded";
  return null;
}

function readObj(err: unknown, key: string): unknown {
  if (typeof err !== "object" || err === null) return undefined;
  return (err as Record<string, unknown>)[key];
}

// ── Copy table ────────────────────────────────────────────────────────────────

function userCopy(code: UserErrorCode, ctx: ClassifyContext): string {
  const noun = entityNoun(ctx);
  switch (code) {
    case "slug_taken":
      // Two flavours: recipe-create says "A recipe with that name already exists"
      // (the most idiomatic phrasing); other entities use the generic shape.
      if (ctx.entity === "recipe" && (ctx.mode === undefined || ctx.mode === "new")) {
        return "A recipe with that name already exists. Choose a different name.";
      }
      return `A ${noun} with that name already exists. Choose a different name.`;
    case "invalid_slug":
      return "Names can only use letters, numbers, and dashes.";
    case "quota_exceeded":
      return "You've reached your plan's limit. Upgrade or remove items to continue.";
    case "ai_ops_exceeded":
      // Static fallback only. classifyMutateError overrides this with a
      // reset-aware line when the error carries `data.resetsAt` (the common
      // path). No "upgrade" salesmanship on the assistant cap (C-WP4 §Quota UX).
      return "You've used all your assistant operations for now. Check Settings → Plan for the reset date.";
    case "group_read_only":
      // Says WHAT is true, nothing about WHOSE plan made it true — a member
      // must never learn another user's commercial state from a refusal
      // (downgrade-safety plan WP-B; branding.md §5.2.1, §8). Byte-identical to
      // the server's own message so the two paths cannot drift.
      return "This group is read-only.";
    case "unauthorised":
      return "Sign in again to continue.";
    case "forbidden":
      return "You don't have access to do that.";
    case "validation":
      return "Some fields need fixing. Check the form and try again.";
  }
}

/**
 * A quiet, unambiguous reset date — e.g. "1 Aug". Formatted in UTC because the
 * `ai_ops` window boundary is defined at 00:00 UTC (C-WP4 window semantics); a
 * user east or west of UTC must not see the boundary shift a calendar day. Year
 * is omitted — the reset is always the NEXT boundary, days/weeks out.
 */
function formatAiOpsResetDate(epochMs: number): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(epochMs));
}

/**
 * At-cap copy for the assistant-ops meter (C-WP4). States what happened + when
 * it resets, reading `data.resetsAt` off the typed `AI_OPS_EXCEEDED` error. No
 * exclamation, no banned word, no "upgrade" (branding.md §5, §5.2.1). When the
 * error carries no reset epoch, falls back to the static Settings pointer.
 */
function aiOpsExceededCopy(err: unknown): string {
  const resetsAt = readNumber(readObj(err, "data"), "resetsAt");
  if (typeof resetsAt === "number" && Number.isFinite(resetsAt)) {
    return `You've used all your assistant operations for now. They reset on ${formatAiOpsResetDate(
      resetsAt,
    )}.`;
  }
  return "You've used all your assistant operations for now. Check Settings → Plan for the reset date.";
}

function systemCopy(code: SystemErrorCode, ctx: ClassifyContext): string {
  const noun = entityNoun(ctx);
  switch (code) {
    case "network":
      return `Couldn't reach the server. Check your network and try again.`;
    case "timeout":
      return `The ${noun} took too long to save. Try again.`;
    case "server":
      return `Couldn't save the ${noun}. Try again. The error has been logged.`;
    case "unknown":
      return `Couldn't save the ${noun}. Try again. The error has been logged.`;
  }
}

// ── Server-supplied prose ─────────────────────────────────────────────────────

/**
 * Strip a leading machine code from a server message.
 *
 * Server errors arrive as either bare codes (`empty_projection`) or
 * `code: prose` (`empty_projection: This draft has no content yet…`). Only the
 * prose half is worth showing.
 */
function stripLeadingCode(raw: string): string {
  const m = /^[a-z0-9_.-]+:\s+(\S.*)$/.exec(raw.trim());
  return (m?.[1] ?? raw).trim();
}

/**
 * True when `s` reads as a finished human sentence rather than a machine code
 * or a fragment.
 *
 * Deliberately strict: showing the user a half-sentence off the wire is worse
 * than showing the classifier's own copy. A sentence must have some length, at
 * least three words, and terminal punctuation.
 */
function readsAsProse(s: string): boolean {
  if (s.length < 16) return false;
  // `slug_taken`, `QUOTA_EXCEEDED`, `not_found` — no spaces at all.
  if (s.split(/\s+/).length < 3) return false;
  // "INTERNAL SERVER ERROR"-style shouting is a code, not copy.
  if (/^[A-Z0-9 _-]+$/.test(s)) return false;
  // A server message we are willing to render is a finished sentence.
  if (!/[.!?]$/.test(s)) return false;
  return true;
}

/**
 * The server's own user-facing message for this error, or `null` when the
 * error carries only a machine code / a network failure.
 *
 * The server frequently knows exactly what went wrong and says so in brand
 * voice ("You've reached your plan's group limit. Remove a group to continue.",
 * "This draft has no content yet. Add ingredients or a method before saving a
 * version."). Throwing that away and printing "Check your connection" tells the
 * user their NETWORK failed when it did not, so they retry forever.
 */
export function serverErrorMessage(err: unknown): string | null {
  // A fetch-level failure has no server message by definition — never let a
  // browser string ("Failed to fetch") reach the user as if it were ours.
  if (err instanceof TypeError) return null;
  if (readString(err, "name") === "AbortError") return null;

  // The error must actually have come back from the server. An oRPC client
  // error carries an HTTP `status`; a bare `new Error("…")` thrown anywhere in
  // the client does not, and its text is developer-facing.
  const hasResponseShape =
    readNumber(err, "status") !== undefined || readObj(err, "data") !== undefined;
  if (!hasResponseShape) return null;

  const candidates = [
    readString(readObj(err, "data"), "message"),
    readString(err, "message"),
    readString(readObj(err, "cause"), "message"),
  ];
  for (const raw of candidates) {
    if (raw === undefined) continue;
    const stripped = stripLeadingCode(raw);
    if (readsAsProse(stripped)) return stripped;
  }
  return null;
}

/**
 * The copy to show a user for a failed mutate: the SERVER's own message when it
 * has one, else a fallback.
 *
 * Use this in every mutate handler instead of hard-coding a string in `catch`.
 *
 * The fallback is either a `ClassifyContext` (use the entity-flavoured
 * classifier table) or a literal string — for surfaces whose own vocabulary is
 * more precise than the generic entity noun ("version", not "attempt").
 *
 * BUG this closes (deep-QA P2): several handlers hard-coded
 * "…Check your connection and try again." in their catch block and dropped
 * `err` entirely — so a plan-cap breach and an empty draft both rendered as a
 * network failure while the console held the correct, specific, brand-voice
 * server copy.
 */
export function resolveMutateErrorCopy(err: unknown, fallback: ClassifyContext | string): string {
  const server = serverErrorMessage(err);
  if (server !== null) return server;
  return typeof fallback === "string" ? fallback : classifyMutateError(err, fallback).copy;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Classify any thrown error from a mutate call into a user/system split.
 *
 * The function is pure — no side effects, no I/O. Reporting (for system
 * errors) is the caller's responsibility (`./report.ts`).
 *
 * @example
 *   try { await api.recipe.create(input); }
 *   catch (err) {
 *     const result = classifyMutateError(err, { entity: "recipe", mode: "new" });
 *     if (result.kind === "system") {
 *       void reportClientError("recipe_create_failed", result, err, { mode });
 *     }
 *     setError(result.copy);
 *   }
 */
export function classifyMutateError(err: unknown, ctx: ClassifyContext): ClassifiedError {
  // 1. Network-level failures — `fetch` threw before getting a response.
  //    These do NOT have an HTTP status. The TypeError shape is browser-standard
  //    (Chrome: "TypeError: Failed to fetch"; Firefox: "TypeError: NetworkError
  //    when attempting to fetch resource"); aborted requests show "AbortError".
  if (err instanceof TypeError) {
    const m = err.message.toLowerCase();
    if (
      m.includes("failed to fetch") ||
      m.includes("network") ||
      m.includes("load failed") ||
      m.includes("networkerror")
    ) {
      return { kind: "system", code: "network", copy: systemCopy("network", ctx) };
    }
  }
  // DOMException AbortError (also a non-HTTP failure mode).
  const errName = readString(err, "name");
  if (errName === "AbortError") {
    return { kind: "system", code: "network", copy: systemCopy("network", ctx) };
  }

  // 2. oRPC client errors carry a `status` (HTTP) and `code` (oRPC code).
  //    Probe the embedded DO `error` string first — it's the most specific.
  const userCode = findUserErrorCode(err);
  if (userCode !== null) {
    // The assistant-ops cap carries a reset epoch in `data.resetsAt`; its copy
    // is built dynamically (states the reset date). All other user codes use the
    // static entity-flavoured table.
    const copy = userCode === "ai_ops_exceeded" ? aiOpsExceededCopy(err) : userCopy(userCode, ctx);
    return { kind: "user", code: userCode, copy };
  }

  // 3. Status-based fallback for classified user errors.
  const status = readNumber(err, "status");
  if (status === 401) {
    return { kind: "user", code: "unauthorised", copy: userCopy("unauthorised", ctx) };
  }
  if (status === 403) {
    return { kind: "user", code: "forbidden", copy: userCopy("forbidden", ctx) };
  }
  if (status === 422) {
    return { kind: "user", code: "validation", copy: userCopy("validation", ctx) };
  }

  // 4. System-side: timeouts and server errors.
  if (status === 408 || status === 504) {
    return { kind: "system", code: "timeout", copy: systemCopy("timeout", ctx) };
  }
  if (typeof status === "number" && status >= 500 && status < 600) {
    return { kind: "system", code: "server", copy: systemCopy("server", ctx) };
  }

  // 5. Other 4xx without a known message → treat as validation (user error).
  if (typeof status === "number" && status >= 400 && status < 500) {
    return { kind: "user", code: "validation", copy: userCopy("validation", ctx) };
  }

  // 6. Anything else — including plain Error / unknown shapes.
  return { kind: "system", code: "unknown", copy: systemCopy("unknown", ctx) };
}

/**
 * True when an error is an EXPLICITLY aborted fetch (`AbortController.abort()`)
 * rather than a genuine failure. Every modern engine — browsers and undici
 * (Node) — surfaces this as a `DOMException` named `"AbortError"` (spec-defined),
 * whatever the human message ("The user aborted a request." / "The operation was
 * aborted." / "signal is aborted without reason"). So we match by NAME, plus the
 * `DOMException.ABORT_ERR` code (20) as a belt-and-suspenders fallback.
 *
 * Deliberately NOT matched:
 *   - A bare `TypeError: Failed to fetch` — that is the genuine-offline shape AND
 *     the shape a NAVIGATION teardown (net::ERR_ABORTED) takes; the latter is
 *     classified by `~/lib/nav-abort` via page-unload / unmount signals, not here.
 *   - Any TypeError whose message merely CONTAINS "abort"/"cancel" — that would
 *     silence a genuine programming error like
 *     "Cannot read properties of undefined (reading 'cancelToken')".
 *
 * Callers use this to DOWNGRADE abort noise from `console.error` to
 * `console.debug` while keeping real network/server failures at error level.
 */
export function isAbortError(err: unknown): boolean {
  if (readString(err, "name") === "AbortError") return true;
  if (typeof DOMException !== "undefined" && err instanceof DOMException) {
    return err.code === DOMException.ABORT_ERR;
  }
  return false;
}
